import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sparkles, PenLine, Loader2, ArrowRight, BookOpen, BarChart3, Target } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const MODALIDAD_OPTIONS = [
    { value: 'basica_generalista', label: 'Enseñanza Básica — Generalista', icon: '📘', requiere_asignatura: false },
    { value: 'basica_asignatura', label: 'Enseñanza Básica — Asignatura', icon: '📗', requiere_asignatura: true },
    { value: 'media', label: 'Enseñanza Media', icon: '📙', requiere_asignatura: true },
    { value: 'parvularia', label: 'Educación Parvularia', icon: '🧸', requiere_asignatura: false },
];

export default function HomeWritingFirst() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [modal, setModal] = useState('');
    const [asignatura, setAsignatura] = useState('');
    const [saving, setSaving] = useState(false);
    const [starting, setStarting] = useState(false);

    const load = async () => {
        try {
            setLoading(true);
            const r = await api.get('/users/me/first-task');
            setData(r.data);
            if (r.data?.modalidad) setModal(r.data.modalidad);
            if (r.data?.asignatura) setAsignatura(r.data.asignatura);
        } catch (e) {
            toast.error('No pudimos cargar tu portada.');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const guardarModalidad = async () => {
        if (!modal) { toast.error('Selecciona qué Portafolio estás desarrollando.'); return; }
        const option = MODALIDAD_OPTIONS.find((o) => o.value === modal);
        if (option?.requiere_asignatura && !asignatura.trim()) {
            toast.error('Indica la asignatura.'); return;
        }
        setSaving(true);
        try {
            await api.patch('/users/me/modalidad', { modalidad: modal, asignatura: option?.requiere_asignatura ? asignatura.trim() : undefined });
            await load();
            toast.success('Modalidad guardada.');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar modalidad');
        } finally {
            setSaving(false);
        }
    };

    const comenzar = async () => {
        setStarting(true);
        try {
            const r = await api.post('/users/me/first-task/start');
            if (r.data?.draft_id) {
                navigate(`/borradores/${r.data.draft_id}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'No pudimos abrir tu borrador.');
            setStarting(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex h-[60vh] items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
                </div>
            </Layout>
        );
    }

    // SELECTOR DE MODALIDAD (primera vez)
    if (data?.needs_modalidad) {
        const option = MODALIDAD_OPTIONS.find((o) => o.value === modal);
        return (
            <Layout>
                <div className="mx-auto max-w-2xl px-4 py-10">
                    <div className="text-center">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">Comencemos</span>
                        <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
                            ¿Qué Portafolio Docente estás desarrollando?
                        </h1>
                        <p className="mt-3 text-slate-600 text-sm">
                            Esta selección determina qué tareas y rúbricas usará la IA en todas tus mejoras.
                            Podrás cambiarla luego desde tu perfil.
                        </p>
                    </div>
                    <Card className="mt-8">
                        <CardContent className="pt-6 space-y-3">
                            {MODALIDAD_OPTIONS.map((o) => (
                                <button
                                    key={o.value}
                                    onClick={() => setModal(o.value)}
                                    className={`w-full flex items-center gap-3 rounded-lg border-2 p-3 text-left transition-colors ${modal === o.value ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-200' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                                    data-testid={`modalidad-${o.value}`}
                                >
                                    <span className="text-2xl shrink-0" aria-hidden="true">{o.icon}</span>
                                    <span className="font-semibold text-slate-900">{o.label}</span>
                                </button>
                            ))}
                            {option?.requiere_asignatura && (
                                <div className="pt-3">
                                    <label className="block text-xs font-semibold text-slate-700 mb-1">Asignatura</label>
                                    <Input
                                        value={asignatura}
                                        onChange={(e) => setAsignatura(e.target.value)}
                                        placeholder="Ej: Matemática, Lenguaje, Historia…"
                                        data-testid="asignatura-input"
                                    />
                                </div>
                            )}
                            <Button
                                onClick={guardarModalidad}
                                disabled={!modal || saving || (option?.requiere_asignatura && !asignatura.trim())}
                                className="w-full gap-2 mt-2"
                                data-testid="guardar-modalidad-btn"
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                                Continuar
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        );
    }

    // PORTADA PRINCIPAL ORIENTADA AL BORRADOR
    const continuar = Boolean(data?.draft_id && data?.has_content);
    const firstTask = data?.first_task || {};
    const titulo = continuar ? 'Continúa donde quedaste' : 'Comienza trabajando en tu Portafolio';

    return (
        <Layout>
            <div className="mx-auto max-w-5xl px-4 py-10 lg:px-8">
                {/* Banner principal — clicable: abre/continúa el borrador (sin volver a iniciar sesión) */}
                <button
                    type="button"
                    onClick={comenzar}
                    disabled={starting}
                    aria-label="Comienza ahora · abrir mi borrador"
                    data-testid="home-hero-banner"
                    className="mb-10 block w-full overflow-hidden rounded-2xl ring-1 ring-slate-200 shadow-lg shadow-slate-900/10 transition-transform hover:scale-[1.005] focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-300 disabled:cursor-wait"
                >
                    <img
                        src="https://customer-assets-wrfwihn1.emergentagent.net/job_101ec70a-ddc1-481b-9653-c6adf0debcdb/artifacts/eab3d62b7d84633e_Portafolio%20Docente%202026_%20Maximiza%20Tu%20Tiempo%20%281%29.png"
                        alt="Portafolio Docente 2026 · Sin vueltas, con poco tiempo: maximiza tu tiempo"
                        className="block h-auto w-full"
                        loading="eager"
                        decoding="async"
                        width="1536"
                        height="1024"
                        data-testid="home-hero-banner-image"
                    />
                </button>

                {/* Hero */}
                <div className="text-center max-w-3xl mx-auto">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-700">Tu portafolio, un mes para la entrega</span>
                    <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                        Tú haces tu Portafolio.<br />
                        <span className="text-indigo-700">La IA te ayuda a redactarlo mejor.</span>
                    </h1>
                    <p className="mt-4 text-slate-700 text-base leading-relaxed">
                        Escribe tu tarea y recibe dos propuestas de mejora basadas en la rúbrica correspondiente:
                        una orientada a <strong>Competente</strong> y otra a <strong>Destacado</strong>. Puedes editarlas
                        y volver a mejorarlas todas las veces que necesites.
                    </p>
                </div>

                {/* CTA destacado */}
                <Card className="mt-10 border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-slate-50 shadow-sm">
                    <CardHeader>
                        <div className="flex items-start gap-3">
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
                                {continuar ? <PenLine className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
                            </div>
                            <div>
                                <CardTitle className="font-heading text-xl text-slate-900">{titulo}</CardTitle>
                                <CardDescription className="mt-1 text-sm">
                                    {continuar
                                        ? 'Retoma tu última versión guardada y sigue mejorándola.'
                                        : 'Escribe tu respuesta con tus propias experiencias, decisiones y evidencias. Luego utiliza la IA para mejorar su redacción tomando como referencia la rúbrica correspondiente.'}
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-lg bg-white border border-slate-200 p-4" data-testid="first-task-card">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Tarea destacada</div>
                            <div className="mt-1 font-heading text-lg font-bold text-slate-900">{firstTask.indicador}</div>
                            <div className="mt-0.5 text-xs text-slate-500">{firstTask.tarea} · {firstTask.indicador_id}</div>
                        </div>
                        <Button
                            onClick={comenzar}
                            disabled={starting}
                            size="lg"
                            className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700 text-base"
                            data-testid="comenzar-cta-btn"
                        >
                            {starting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                            {continuar ? 'Continuar mi tarea' : '✨ Comenzar a mejorar mi Portafolio'}
                        </Button>
                        <p className="text-xs text-slate-500 text-center">
                            Circuito: escribir → ✨ Mejorar con IA → comparar Competente/Destacado → editar → evaluar
                        </p>
                    </CardContent>
                </Card>

                {/* Accesos secundarios */}
                <div className="mt-10 grid gap-3 sm:grid-cols-3">
                    <button onClick={() => navigate('/dashboard')} className="group rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-slate-300 hover:shadow-sm transition" data-testid="link-dashboard">
                        <BookOpen className="h-5 w-5 text-slate-500 group-hover:text-indigo-600 transition" />
                        <div className="mt-2 font-semibold text-slate-900">Dashboard</div>
                        <div className="text-xs text-slate-500">Vista general y asistente estratégico</div>
                    </button>
                    <button onClick={() => navigate('/tareas')} className="group rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-slate-300 hover:shadow-sm transition" data-testid="link-tareas">
                        <Target className="h-5 w-5 text-slate-500 group-hover:text-indigo-600 transition" />
                        <div className="mt-2 font-semibold text-slate-900">Todas las tareas</div>
                        <div className="text-xs text-slate-500">Explora cada tarea y sus indicadores</div>
                    </button>
                    <button onClick={() => navigate('/progreso')} className="group rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-slate-300 hover:shadow-sm transition" data-testid="link-progreso">
                        <BarChart3 className="h-5 w-5 text-slate-500 group-hover:text-indigo-600 transition" />
                        <div className="mt-2 font-semibold text-slate-900">Mi progreso</div>
                        <div className="text-xs text-slate-500">Avance por módulo y por tarea</div>
                    </button>
                </div>
            </div>
        </Layout>
    );
}
